import React, { useState, useEffect } from 'react';
import { Spin, Typography, Button, Table, Modal, message, Input } from 'antd';
import { PlusOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import { debounce, get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import ContentPageHeader from '../../components/contentPageHeader';
import { CREATE_CAMPAIGN, CAMPAIGN_DETAILS } from '../../constants/routes';
import { campaignManagement } from '../../redux/actions/campaignAction';
import { salesChannelManagement } from '../../redux/actions/marketPlaceAction';
import {
  generatePaginationObj,
  replaceMultiple,
  hasAnyPermission,
} from '../../utilities/helpers';
import TableFilters from '../../components/tableFilters';
import {
  campaignTableColumns,
  campaignTableFiltersList,
} from '../../constants/campaign';
import ShowPaginationLimits from '../../components/showPaginationLimits';
import {
  DELETE_CAMPAIGN_SUCCESS,
  UPDATE_CAMPAIGN_SUCCESS,
} from '../../constants/messages';
import { ROLE_ADMIN, ROLE_MANAGER } from '../../constants/roles';

const { Text } = Typography;
const { confirm } = Modal;

const CampaignScreen = () => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const role = useSelector((state) => get(state, 'auth.role'));
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const isBrandUser = hasAnyPermission(
    [ROLE_ADMIN, ROLE_MANAGER],
    [get(role, 'role_code')]
  );

  const userComapnies = useSelector((state) =>
    get(state, 'auth.userCompanies')
  );

  const dispatch = useDispatch();
  const [campaignList, setCampaignList] = useState({
    data: [],
    sorters: null,
    filters: null,
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
    filterChips: null,
  });
  const [channelList, setChannelList] = useState([]);
  const [touchpointCitiesList, setTouchpointCitiesList] = useState([]);
  const [offeringCodeList, setOfferingCodeList] = useState([]);
  const [tempFilter, setTempFilter] = useState({
    filterDropdown: {
      brand: false,
      channel: false,
      touchpointCities: false,
      status: false,
      offeringCode: false,
    },
    tempFilterChips: {
      company_id: undefined,
      sales_channel_id: undefined,
      touchpoint_city_id: undefined,
      is_active: undefined,
      offering_id: undefined,
    },
    filterValues: {
      company_id: undefined,
      sales_channel_id: undefined,
      touchpoint_city_id: undefined,
      is_active: undefined,
      offering_id: undefined,
    },
  });

  const { offset, limit } = generatePaginationObj(
    get(campaignList, 'pagination.current'),
    get(campaignList, 'pagination.limit')
  );

  const fetchChannelList = () => {
    setLoading(true);
    dispatch(salesChannelManagement('compact.list.sales_channels'))
      .then((result) => {
        setLoading(false);
        setChannelList(get(result, 'data'));
      })
      .then(() => {
        setLoading(false);
      });
  };

  const fetchTouchpointCitiesList = () => {
    setLoading(true);
    dispatch(campaignManagement('list.touchpoint_cities'))
      .then((result) => {
        setLoading(false);
        setTouchpointCitiesList(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchOfferingCodeList = () => {
    setLoading(true);
    dispatch(
      campaignManagement('list.offering_codes', {
        company_id: isBrandUser ? get(selectedCompany, 'id') : undefined,
      })
    )
      .then((result) => {
        setLoading(false);
        setOfferingCodeList(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchCampaignList = (
    page = 1,
    filterObj = {},
    sorterObj = {},
    filterChipsObj = {},
    perPageLimit = get(campaignList, 'pagination.limit')
  ) => {
    setLoading(true);
    const pageObj = generatePaginationObj(page, perPageLimit);
    dispatch(
      campaignManagement('list.campaigns', {
        company_id: isBrandUser ? get(selectedCompany, 'id') : undefined,
        ...pageObj,
        filters: filterObj,
      })
    )
      .then((result) => {
        setLoading(false);
        setCampaignList({
          data: get(result, 'data'),
          sorters: sorterObj,
          filters: filterObj,
          pagination: {
            ...pageObj,
            total: get(result, 'total'),
            current: page,
          },
          filterChips: filterChipsObj,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchChannelList();
    fetchTouchpointCitiesList();
    fetchOfferingCodeList();
    fetchCampaignList();
  }, []);

  const onViewDetails = (campaign) => {
    history.push(
      replaceMultiple(CAMPAIGN_DETAILS, {
        ':id': get(campaign, 'id'),
        ':companyId': get(campaign, 'company_id'),
      })
    );
  };

  const onDeleteCampaign = (payload) => {
    setLoading(true);
    dispatch(campaignManagement('delete.campaign', payload))
      .then(() => {
        setLoading(false);
        message.success(DELETE_CAMPAIGN_SUCCESS);
        fetchCampaignList(
          get(campaignList, 'pagination.current'),
          get(campaignList, 'filters'),
          get(campaignList, 'sorters'),
          get(tempFilter, 'tempFilterChips')
        );
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onConfirmDelete = (record) => {
    confirm({
      title: 'Are you sure!',
      icon: <ExclamationCircleFilled />,
      content: 'Do you want to delete this Campaign?',
      onOk() {
        onDeleteCampaign({
          id: get(record, 'id'),
          company_id: get(record, 'company_id'),
        });
      },
      onCancel() {},
    });
  };

  const updateCampaignStatus = (id, companyId, isChecked) => {
    setLoading(true);
    const payload = {
      id,
      company_id: companyId,
      is_active: isChecked,
    };
    dispatch(campaignManagement('update.campaign', payload))
      .then(() => {
        setLoading(false);
        message.success(UPDATE_CAMPAIGN_SUCCESS);
        fetchCampaignList(get(campaignList, 'pagination.current'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onCampaignSearch = debounce((initialValue) => {
    fetchCampaignList(
      get(campaignList, 'pagination.current'),
      {
        ...get(campaignList, 'filters'),
        name: initialValue,
      },
      get(campaignList, 'sorters'),
      get(campaignList, 'filterChips')
    );
  }, 500);

  return (
    <Spin spinning={loading}>
      <div>
        <div>
          <ContentPageHeader
            border
            title={[
              <Text className="content-page-header-heading">
                Campaign List
              </Text>,
            ]}
            extra={[
              <Link to={CREATE_CAMPAIGN} key="1">
                <Button
                  className="default-primary-btn"
                  type="primary"
                  icon={<PlusOutlined />}
                >
                  Add New Campaign
                </Button>
              </Link>,
            ]}
          />
        </div>

        <div className="mt-30" style={{ width: '220px' }}>
          <Input
            placeholder="Search by Campaign Name"
            onChange={(e) => {
              onCampaignSearch(e.target.value);
            }}
          />
        </div>

        <div className="d-flex filter" style={{ alignItems: 'flex-start' }}>
          <TableFilters
            filtersList={campaignTableFiltersList(
              userComapnies,
              channelList,
              touchpointCitiesList,
              offeringCodeList
            )}
            tableData={campaignList}
            fetchTableData={fetchCampaignList}
            tempFilter={tempFilter}
            setTempFilter={setTempFilter}
            roleCode={get(role, 'role_code')}
          />
        </div>

        <div className="d-flex" style={{ justifyContent: 'space-between' }}>
          <ShowPaginationLimits
            tableData={campaignList}
            offset={offset}
            limit={limit}
            fetchTableData={fetchCampaignList}
            filterChips={get(tempFilter, 'tempFilterChips')}
          />
        </div>

        <div className="product-table-container">
          <Table
            size="small"
            rowKey="id"
            className="product-table"
            dataSource={get(campaignList, 'data')}
            columns={campaignTableColumns(
              onViewDetails,
              updateCampaignStatus,
              onConfirmDelete
            )}
            pagination={{
              showSizeChanger: false,
              position: ['bottomCenter'],
              hideOnSinglePage: true,
              pageSize: get(campaignList, 'pagination.limit'),
              total: get(campaignList, 'pagination.total'),
              current: get(campaignList, 'pagination.current'),
            }}
            onChange={(paginationObj, filterObj, sorterObj) => {
              fetchCampaignList(
                get(paginationObj, 'current'),
                get(campaignList, 'filters'),
                get(sorterObj, 'order')
                  ? {
                      sortBy: get(sorterObj, 'field'),
                      sortDir:
                        get(sorterObj, 'order') === 'ascend' ? 'ASC' : 'DESC',
                    }
                  : {},
                get(tempFilter, 'tempFilterChips')
              );
            }}
          />
        </div>
      </div>
    </Spin>
  );
};

export default CampaignScreen;
